// Corporate invoice lines (audit FIN-13 / FIN-15, owner 26 Sep 2026).
//
// Each category has its own VAT rule instead of one "5 % on the whole amount" tick box:
//   Rent / Other          the amount is net; VAT 5 % on top when ticked (Rent ticked by default)
//   Traffic / Parking fine the amount is the fine; + 5 % service fee (net) + VAT 5 % on the fee only
//                          (500 -> 500 + 25 + 1.25 = 526.25)
//   Salik                 the amount is the gate tolls, which already hold their VAT; + AED 1.05 a trip
//                          (AED 1 + its VAT, TRIP_SURCHARGE - the same rule everywhere in Yelo):
//                          tolls + trips x 1.05, VAT shown = tolls / 21 + trips x 0.05
//                          (tolls 42.00 over 10 trips -> 52.50, VAT 2.50)
// Every line's Net / VAT / Total is rounded to fils and the invoice's Subtotal / VAT / Total are the sums of
// the rounded lines, so the printed lines always add up to the printed total (FIN-15).
import { parseAmount, netLine, fineLine, salikLine, invoiceTotals } from '../../../constants/finance';

export const CATEGORIES = [
    { id: 'rent', label: 'Rent', rule: 'vat', vatByDefault: true },
    { id: 'salik', label: 'Salik', rule: 'salik' },
    { id: 'parking', label: 'Parking Fine', rule: 'fine' },
    { id: 'traffic', label: 'Traffic Fine', rule: 'fine' },
    { id: 'other', label: 'Other', rule: 'vat', vatByDefault: false },
];

export const categoryOf = (id) => CATEGORIES.find((c) => c.id === id) || CATEGORIES[0];

/** A new line of the given category. */
export const newItem = (category = 'rent', extra = {}) => ({
    id: Date.now() + Math.random(),
    category,
    description: '',
    amount: '',
    trips: category === 'salik' ? '1' : '',
    includeVAT: !!categoryOf(category).vatByDefault,
    ...extra,
});

/** A line after its category changed: the VAT tick box and the trips follow the new category. */
export const withCategory = (item, category) => ({
    ...item,
    category,
    includeVAT: !!categoryOf(category).vatByDefault,
    trips: category === 'salik' ? (item.trips || '1') : '',
});

const fix2 = (n) => (Number(n) || 0).toFixed(2);

/** {net, vat, total, note} of one line; `note` says how a fine / Salik line was built. */
export function corporateLine(item) {
    const cat = categoryOf(item.category);
    const amount = parseAmount(item.amount) ?? 0;
    if (cat.rule === 'fine') {
        const l = fineLine(amount);
        return { net: l.net, vat: l.vat, total: l.total, note: `fine AED ${fix2(l.fine)} + 5% service fee AED ${fix2(l.fee)}; VAT on the fee` };
    }
    if (cat.rule === 'salik') {
        const trips = Math.max(0, parseInt(item.trips, 10) || 0);
        const l = salikLine(amount, trips);
        return { ...l, note: `tolls AED ${fix2(amount)} incl. VAT + ${trips} trip(s) x AED 1.05` };
    }
    return { ...netLine(amount, { vat: !!item.includeVAT }), note: '' };
}

/** The description printed on the invoice line. */
export function lineDescription(item) {
    const cat = categoryOf(item.category);
    const { note } = corporateLine(item);
    const base = item.description ? `[${cat.label}] ${item.description}` : cat.label;
    return note ? `${base} (${note})` : base;
}

/** Subtotal / VAT / Total of the invoice: the sums of the rounded lines. */
export const corporateTotals = (items) => invoiceTotals((items || []).map(corporateLine));
